import { useEffect, useRef, useState } from 'react';

import { Icon, IconType } from '../../components/icons/Icon';
import { Currency } from '../../types/currency';
import styles from './CurrencyDropdown.module.less';

interface CurrencyDropdownProps {
  value: Currency;
  options: Currency[];
  onChange: (next: Currency) => void;
}

function flagIcon(currency: Currency): IconType {
  return currency.toLowerCase() as IconType;
}

export function CurrencyDropdown({ value, options, onChange }: CurrencyDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <Icon className={styles.flag} type={flagIcon(value)} aria-hidden />
        <span className={styles.code}>{value}</span>
        <Icon className={styles.chevron} type="chevronDown" aria-hidden />
      </button>
      {open && (
        <ul className={styles.menu} role="listbox">
          {options.map(opt => {
            const active = opt === value;
            return (
              <li key={opt}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`${styles.option} ${active ? styles.optionActive : ''}`}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  <Icon className={styles.optionFlag} type={flagIcon(opt)} aria-hidden />
                  <span className={styles.optionCode}>{opt}</span>
                  {active && <Icon className={styles.optionCheck} type="checkmark" color="currentColor" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
